import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { workflowAPI } from '../../lib/api';
import { CHANNEL_META, errMsg } from '../../lib/workflow';
import { Button } from '../ui/button';
import { StagePill } from './StagePill';
import { WorkflowStepper } from './WorkflowStepper';
import { CommentThread } from './CommentThread';
import { ChannelChips } from './ChannelChips';
import { RequestChangesDialog } from './RequestChangesDialog';
import { MarkLiveDialog } from './MarkLiveDialog';

const btn = 'h-9 rounded-xl text-xs font-bold transition-transform active:scale-95';

const Actions = ({ item, has, busy, run, setChangesFor, setLiveFor, onOpenMarketplace, onChanged }) => {
  const stage = item.stage;
  const wantsVisuals = ['new', 'in_visuals', 'changes_requested'].includes(stage);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="workflow-actions">
      {has('creator') && wantsVisuals && (
        <Button onClick={() => run('submit')} disabled={!item.media_count || busy} title={item.media_count ? '' : 'Upload at least one photo below first'} className={`${btn} bg-[#E07A5F] hover:bg-[#D0694E] text-white disabled:opacity-40`} data-testid="workflow-submit-review-btn">
          {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null}
          {stage === 'changes_requested' ? 'Resubmit for review 🚀' : 'Submit for review 🚀'}
        </Button>
      )}
      {has('creator') && wantsVisuals && !item.media_count && <span className="text-[11px] text-[#9A8E85]">📸 Upload visuals in the Media section below first</span>}
      {has('reviewer') && stage === 'in_review' && (
        <>
          <Button onClick={() => run('approve')} disabled={busy} className={`${btn} bg-[#81B29A] hover:bg-[#6FA38B] text-white`} data-testid="workflow-approve-btn">Approve ✅</Button>
          <Button variant="outline" onClick={() => setChangesFor(item)} className={`${btn} border-[#FECDD3] text-[#9F1239] hover:bg-[#FFF1F2]`} data-testid="workflow-request-changes-btn">Request changes 🔁</Button>
        </>
      )}
      {has('reviewer') && stage === 'approved' && (
        <>
          <Button onClick={() => onOpenMarketplace('etsy', 'push')} className={`${btn} bg-[#F97316] hover:bg-[#EA580C] text-white`} data-testid="workflow-open-etsy-push-btn">
            {item.etsy_listing?.listing_id ? 'Etsy draft pushed: update it 🛒' : 'Open Etsy push panel 🛒'}
          </Button>
          <Button onClick={() => setLiveFor(item)} className={`${btn} bg-[#2D2623] hover:bg-black text-white`} data-testid="workflow-mark-live-btn">Mark live on Etsy 🛍️</Button>
          <Button variant="ghost" onClick={() => setChangesFor(item)} className={`${btn} text-[#9F1239]`} data-testid="workflow-send-back-btn">Send back 🔁</Button>
        </>
      )}
      {['live_on_etsy', 'done'].includes(stage) && (
        <div className="w-full space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#9A8E85]">🌐 Other channels {has('distributor') ? '(tap to open / tick off)' : ''}</p>
          <ChannelChips item={item} canEdit={has('distributor')} onOpen={(ch) => onOpenMarketplace(CHANNEL_META[ch].mp)} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
};

export const WorkflowBar = ({ productId, mediaCount, onOpenMarketplace }) => {
  const [item, setItem] = useState(null);
  const [roles, setRoles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [changesFor, setChangesFor] = useState(null);
  const [liveFor, setLiveFor] = useState(null);
  const has = (r) => roles.includes(r);

  const load = useCallback(async () => {
    try {
      const { data } = await workflowAPI.item(productId);
      const { roles: rs, ...rest } = data;
      setItem(rest);
      setRoles(rs || []);
    } catch { /* not critical */ }
  }, [productId]);
  useEffect(() => { load(); }, [load, mediaCount]);

  const run = async (kind) => {
    setBusy(true);
    try {
      if (kind === 'submit') {
        setItem(await workflowAPI.submitReview(productId).then((r) => r.data));
        toast.success('Sent to Nayan for review 👀🚀');
      } else {
        setItem(await workflowAPI.approve(productId).then((r) => r.data));
        toast.success('Approved ✅ Review the content and push it to Etsy below 👇');
        onOpenMarketplace('etsy', 'push');
      }
    } catch (e) {
      toast.error(errMsg(e, 'Action failed'));
    } finally {
      setBusy(false);
    }
  };

  if (!item) return null;
  const wf = item.workflow;
  if (!wf) {
    if (!has('creator')) return null;
    return (
      <div className="wf-card p-4 flex flex-wrap items-center justify-between gap-3 bg-[#FDFBF7]" data-testid="workflow-bar-enroll">
        <p className="text-sm text-[#6E635B]">🗂️ Not in the review pipeline yet. Upload the visuals, then send it to Nayan.</p>
        <Button onClick={() => run('submit')} disabled={!item.media_count || busy} className={`${btn} bg-[#E07A5F] hover:bg-[#D0694E] text-white disabled:opacity-40`} data-testid="workflow-enroll-submit-btn">Submit for review 🚀</Button>
      </div>
    );
  }

  return (
    <div className="wf-card wf-rise p-5 space-y-4 bg-gradient-to-br from-white to-[#FDFBF7]" data-testid="workflow-bar">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-jakarta text-base font-bold text-[#2D2623] flex items-center gap-2">🧭 Listing pipeline <StagePill stage={item.stage} size="xs" testId="workflow-stage-pill" /></h2>
        <span className="text-[11px] text-[#9A8E85]">🔗 by {wf.submitted_by_name || 'team'}{wf.review_rounds ? ` · 🔄 ${wf.review_rounds} review round${wf.review_rounds > 1 ? 's' : ''}` : ''}</span>
      </div>
      <WorkflowStepper stage={item.stage} />
      <Actions item={item} has={has} busy={busy} run={run} setChangesFor={setChangesFor} setLiveFor={setLiveFor} onOpenMarketplace={onOpenMarketplace} onChanged={setItem} />
      <CommentThread item={item} canComment={roles.length > 0} onChanged={setItem} />
      <RequestChangesDialog item={changesFor} onClose={() => setChangesFor(null)} onDone={setItem} />
      <MarkLiveDialog item={liveFor} onClose={() => setLiveFor(null)} onDone={setItem} />
    </div>
  );
};
